import { Checkbox, ContainerTitle, ContainerTitleAction } from '../layout.jsx';
import { getMeta } from '../meta.js';
import { showMessageBox } from '../msgbox.jsx';
import xhr from '../xhr.js';

const ATTACH_PREFIX = 'sbg-';
const BG_URL_VAR = '--site-bg-image';
const BG_WIDTH_VAR = '--background-width';
const BG_HEIGHT_VAR = '--background-height';

// mirrors \Misuzu\Profile\ProfileBackgroundAttach
const Attach = [
    { value: 'cover', label: 'Cover the page' },
    { value: 'stretch', label: 'Stretch across the page' },
    { value: 'tile', label: 'Tile across the page' },
    { value: 'contain', label: 'Contain within the page' },
];
const Attrs = [
    { value: 'blend', label: 'Blend with accent colour' },
    { value: 'slide', label: 'Slide towards the bottom right' },
];

function getBgAttach() {
    for(const { value } of Attach)
        if(document.body.classList.contains(ATTACH_PREFIX + value))
            return value;

    return null;
}

function setBgAttach(attach) {
    for(const { value } of Attach)
        document.body.classList.toggle(ATTACH_PREFIX + value, value === attach);
}

function getBgAttrs() {
    const attrs = {};
    for(const { value } of Attrs)
        attrs[value] = document.body.classList.contains(ATTACH_PREFIX + value);

    return attrs;
}

function newBgAttrs() {
    const attrs = {};
    for(const { value } of Attrs)
        attrs[value] = false;

    return attrs;
}

function setBgAttrs(attrs) {
    for(const { value } of Attrs)
        if(value in attrs)
            document.body.classList.toggle(ATTACH_PREFIX + value, !!attrs[value]);
}

function removeBgAttrs() {
    for(const { value } of Attrs)
        document.body.classList.remove(ATTACH_PREFIX + value);
}

function getBgImage() {
    return new Promise(resolve => {
        const style = getComputedStyle(document.body);

        let url = style.getPropertyValue(BG_URL_VAR);
        if(!url) return void resolve(null);

        if(url.startsWith("url('") && url.endsWith("')"))
            url = url.substring(url.indexOf("url('") + 5, url.indexOf("')"));
        else if(url.startsWith('url("') && url.endsWith('")'))
            url = url.substring(url.indexOf('url("') + 5, url.indexOf('")'));
        else return void resolve(null);
        if(!url) return void resolve(null);

        const image = new Image;

        const width = style.getPropertyValue(BG_WIDTH_VAR);
        if(width.endsWith('px'))
            image.width = parseInt(width.substring(0, width.length - 2));

        const height = style.getPropertyValue(BG_HEIGHT_VAR);
        if(height.endsWith('px'))
            image.height = parseInt(height.substring(0, height.length - 2));

        image.alt = 'background image';

        image.onerror = () => void resolve(null);
        image.onload = () => void resolve(image);

        image.src = url;
    });
}

function setBgImage(image) {
    if(!(image instanceof Image))
        throw new Error('parameter image must be an Image object');

    document.body.style.setProperty(BG_URL_VAR, `url('${image.src}')`);
    document.body.style.setProperty(BG_WIDTH_VAR, `${image.width}px`);
    document.body.style.setProperty(BG_HEIGHT_VAR, `${image.height}px`);
}

function removeBgImage(override=false) {
    document.body.style.setProperty(BG_URL_VAR, 'initial');
    document.body.style.setProperty(BG_WIDTH_VAR, 'auto');
    document.body.style.setProperty(BG_HEIGHT_VAR, 'auto');
}

(() => {
    const backgroundUri = getMeta('profile-background');
    if(typeof backgroundUri !== 'string')
        return;

    const profileRoot = document.querySelector('.js-profile');
    if(!profileRoot)
        return;

    const editBackground = document.querySelector('.js-profile-background');
    if(!(editBackground instanceof HTMLButtonElement))
        return;

    let backgroundEditor;
    editBackground.onclick = async () => {
        if(backgroundEditor) return;

        profileRoot.classList.add('profile-translucent');

        const initialImage = await getBgImage();
        const initialAttach = initialImage === null ? null : getBgAttach();
        const initialAttrs = Object.freeze(initialImage === null ? newBgAttrs() : getBgAttrs());

        let prevImage = initialImage;
        let prevAttach = initialAttach;
        let prevAttrs = Object.assign({}, initialAttrs);

        let enableToggle, uploadLabel, submitButton;
        let imageWrap, attachWrap, attrsWrap;

        const removeForm = () => {
            backgroundEditor.remove();
            backgroundEditor = null;
            profileRoot.classList.remove('profile-translucent');
        };

        const removeBg = () => {
            removeBgImage();
            setBgAttach(null);
            removeBgAttrs();
        };

        const restoreInitial = () => {
            if(initialImage === null) {
                removeBg();
            } else {
                setBgImage(initialImage);
                setBgAttach(initialAttach);
                setBgAttrs(initialAttrs);
            }
        };

        const restorePrevious = () => {
            if(prevImage === null) {
                removeBg();
            } else {
                setBgImage(prevImage);
                setBgAttach(prevAttach);
                setBgAttrs(prevAttrs);
            }
        };

        const formOnSubmit = async function(ev) {
            ev.preventDefault();

            if(this.dataset.disabled)
                return;
            this.dataset.disabled = '1';
            this.classList.add('profile-background-editor-busy');

            try {
                for(let i = 0; i < this.elements.length; ++i) {
                    const element = this.elements[i];
                    element.dataset.disabled = element.disabled ? '1' : '';
                    element.disabled = true;
                }

                let status, body;

                if(!this.elements.enable.checked) {
                    const result = await xhr.delete(backgroundUri, { authed: true, csrf: true });
                    status = result.status;
                } else {
                    const params = new FormData;
                    params.set('attach', this.elements.attach.value);
                    for(const { value } of Attrs)
                        params.set(value, this.elements[value].checked ? this.elements[value].value : '');
                    if(this.elements.image.files.length > 0)
                        params.set('image', this.elements.image.files[0]);

                    const result = await xhr.put(backgroundUri, { authed: true, csrf: true, type: 'json' }, params);
                    status = result.status;
                    body = result.body;
                }

                switch(status) {
                case 204: break;
                case 404: throw new Error('Profile not found.');
                case 403: throw new Error('You are not allowed to change this profile background.');
                case 400: throw new Error(body?.error?.text ?? 'Something went wrong while processing your background change.');
                default: throw new Error(`Received unexpected HTTP status code ${status}.`);
                }

                removeForm();
            } catch(ex) {
                await showMessageBox(ex, 'Background');
            } finally {
                for(let i = 0; i < this.elements.length; ++i) {
                    const element = this.elements[i];
                    element.disabled = element.dataset.disabled !== '';
                    delete element.dataset.disabled;
                }

                this.classList.remove('profile-background-editor-busy');
                delete this.dataset.disabled;
            }
        };

        const cancelOnClick = () => {
            restoreInitial();
            removeForm();
        };

        const enableOnChange = function() {
            imageWrap.hidden = attachWrap.hidden = attrsWrap.hidden = !this.checked;
            if(this.checked)
                restorePrevious();
            else
                removeBg();
        };
        const fileOnChange = function() {
            if(this.files.length < 1) {
                restoreInitial();
                return;
            }

            const image = new Image;
            image.onload = () => {
                setBgImage(prevImage = image);
                setBgAttach(prevAttach ??= Attach[0].value);
                setBgAttrs(prevAttrs);
            };
            image.onerror = () => {
                this.value = '';
                restoreInitial();
                showMessageBox('Failed to load selected image.', 'Background');
            };
            image.src = URL.createObjectURL(this.files[0]);
        };
        const attachOnChange = function() {
            prevAttach = this.value;
            if(prevImage !== null) setBgAttach(this.value);
        };
        const attrOnChange = function() {
            prevAttrs[this.name] = this.checked;
            if(prevImage !== null) setBgAttrs({ [this.name]: this.checked });
        };

        profileRoot.parentNode.insertBefore(backgroundEditor = <form class="container profile-background-editor" onsubmit={formOnSubmit}>
            <ContainerTitle title="Background">
                {submitButton = <ContainerTitleAction type="submit" title="Save"><i class="fa-solid fa-check" /></ContainerTitleAction>}
                <ContainerTitleAction type="button" title="Cancel" onclick={cancelOnClick}><i class="fa-solid fa-xmark" /></ContainerTitleAction>
            </ContainerTitle>
            <div class="profile-background-editor-body">
                <div class="profile-background-editor-enable">
                    {enableToggle = <Checkbox name="enable" checked={initialImage !== null} onchange={enableOnChange}>Display background</Checkbox>}
                </div>
                {imageWrap = <div class="profile-background-editor-file" hidden={initialImage === null}>
                    {uploadLabel = <label class="input__button">
                        <input type="file" name="image" accept="image/*" onchange={fileOnChange} />
                        <span>Select image...</span>
                    </label>}
                </div>}
                {attachWrap = <div class="profile-background-editor-attach" hidden={initialImage === null}>
                    <select class="input__select" name="attach" onchange={attachOnChange}>
                        {...Attach.map(({ value, label }) => <option value={value} selected={value === initialAttach}>{label}</option>)}
                    </select>
                </div>}
                {attrsWrap = <div class="profile-background-editor-attribs" hidden={initialImage === null}>
                    {...Attrs.map(({ value, label }) => <Checkbox name={value} checked={initialAttrs[value]} onchange={attrOnChange}>{label}</Checkbox>)}
                </div>}
            </div>
        </form>, profileRoot.nextSibling ?? profileRoot);

        (imageWrap.hidden ? enableToggle : uploadLabel).focus();
    };
})();
